// The tiered-scoring-team CASH board — separate mode, and only once the event shows money.
//
// TeamStandings (the sibling file) ranks a team by golf: to-par, thru. This ranks the same
// teams by side-game cash instead, because the reason a second team exists in separate mode
// is to pool everyone's individual side-game winnings under one banner even though its
// members never share a card. Each player's own figure still comes from their OWN
// foursome's games — computed once, on the individual board, and handed in here — never
// recomputed differently, which is why the header line says so rather than letting a reader
// assume the team played one shared game together.
//
// Presentation only, same discipline as TeamStandings: the ranking itself comes from
// `scoringTeamCashBoard` in the shared engine (unused directly here) via
// `scoringTeamCashRowsFromPayload` on the public board. Self-contained stylesheet for the
// same reason TeamStandings carries its own — only one of `.ev-*` / `.lb-*` is ever loaded
// on a given page.

import { fmtMoney } from '@shared/sync/toDomain';
import type { TeamCashRow } from '@shared/engine/tieredTeams';
import { TEAM_CASH_HEADER } from '../lib/scoringTeams';

export type { TeamCashRow } from '@shared/engine/tieredTeams';
type TeamCashMember = TeamCashRow['members'][number];

/** "1" or "T1" — same tie convention as TeamStandings' own `positionLabel`. */
function positionLabel(row: TeamCashRow, rows: TeamCashRow[]): string {
  if (row.position === null) return '—';
  const tied = rows.some((r) => r.id !== row.id && r.position === row.position);
  return tied ? `T${row.position}` : `${row.position}`;
}

/** "PJ" for "Patrick Jones", the single initial for a one-word name. Terse on purpose — a
 *  cash row already carries up to eight names and their own figures in one line. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '—';
  return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
}

function MemberList({ members }: { members: TeamCashMember[] }) {
  if (members.length === 0) return <span>No players yet</span>;
  return (
    <>
      {members.map((m, i) => (
        <span key={m.id}>
          {i > 0 ? ', ' : ''}
          {initials(m.name)}
          <span className="tt-cash-own">{fmtMoney(m.moneyCents)}</span>
        </span>
      ))}
    </>
  );
}

export function TeamCashStandings({ rows }: { rows: TeamCashRow[] }) {
  if (rows.length === 0) return null;

  return (
    <div className="card tt-card" style={{ marginTop: 10 }}>
      <style>{`
.tt-head { margin: 0 0 10px; font-size: 13px; color: var(--ink-dim); font-weight: 600; }
.tt-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.tt-players { margin-top: 3px; color: var(--ink-dim); font-size: 12px; }
.tt-cash-own { margin-left: 3px; opacity: .55; font-size: 11px; font-weight: 600; }
`}</style>
      <p className="tt-head">Cash · {TEAM_CASH_HEADER}</p>
      <div className="tt-scroll">
        <table>
          <thead>
            <tr>
              <th style={{ width: 48 }}>Pos</th>
              <th>Team</th>
              <th style={{ width: 90 }}>Cash</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td className="tnum">{positionLabel(row, rows)}</td>
                <td>
                  <strong>{row.name}</strong>
                  <div className="tt-players">
                    <MemberList members={row.members} />
                  </div>
                </td>
                <td className="tnum">{fmtMoney(row.cashCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default TeamCashStandings;
